import { useGeneratedEmployeeNotificationStore } from '@/stores'
import type { EmployeeNotification, EmployeeNotificationJsonldDefaultRead } from '@/api'
import { DateTime } from 'luxon'
import _, { random } from 'lodash-es'
import { useTitrop } from '@/composables/useTitrop.ts'
import { useMercure } from '@/composables/useMercure.ts'
import { type Component, computed, type ComputedRef, markRaw, ref, type Ref } from 'vue'
import GenericPanel from '@/components/notifications/GenericPanel.vue'
import UnknownTypePanel from '@/components/notifications/UnknownTypePanel.vue'
import { default as RegisterNotificationPanels } from '@/components/notification-panels/register'
import { useLogger } from '@/composables/useLogger.ts'
import { useLocalNotificationStore } from '@/stores/LocalNotification.store'

import EstimationRequestPanel from '@/components/notifications/EstimationRequestPanel.vue'
import EffortAssignmentPanel from '@/components/notifications/EffortAssignmentPanel.vue'
import EffortNoteMentionPanel from '@/components/notifications/EffortNoteMentionPanel.vue'
import { useTitropEvents } from '@/composables/useTitropEvents.ts'

type Writeable<T> = { -readonly [P in keyof T]: T[P] }
type WriteableNotification = Writeable<{ -readonly [P in keyof EmployeeNotification]: EmployeeNotification[P] }>

export interface LocalNotificationBaseNonGeneric extends Omit<WriteableNotification, 'type' | 'data'> {
  type: Uppercase<string>
  data: unknown
  deletable?: boolean
}

/**
 *  @description Gets a ref to an application wide config (read-only!) - ALWAYS use '.value'!
 *  @template T notification type for identifying component
 *  @template U type defining the inner data of notification
 * */
export interface LocalNotificationBase<T extends Uppercase<string>, U extends object | unknown = unknown> extends LocalNotificationBaseNonGeneric {
  type: T
  data: U
}

type AnyNotification = LocalNotificationBaseNonGeneric | EmployeeNotification

const logger = useLogger('InAppNotification')

class _InAppNotificationService {
  readonly #remoteNotifications: Ref<EmployeeNotificationJsonldDefaultRead[]> = ref<EmployeeNotificationJsonldDefaultRead[]>([])
  readonly #notifications: ComputedRef<(EmployeeNotification | LocalNotificationBaseNonGeneric)[]> = computed(() => [...useLocalNotificationStore().GetNotifications(), ...this.#remoteNotifications.value])
  readonly #notificationsUnread: ComputedRef<(EmployeeNotificationJsonldDefaultRead | LocalNotificationBaseNonGeneric)[]> = computed(() => {
    return this.#notifications.value.filter((n) => !n.read)
  })
  readonly #notificationsRead: ComputedRef<(EmployeeNotificationJsonldDefaultRead | LocalNotificationBaseNonGeneric)[]> = computed(() => {
    return this.#notifications.value.filter((n) => n.read)
  })
  readonly #notificationCount: ComputedRef<number> = computed(() => this.#notifications.value.length)
  readonly #notificationUnreadCount: ComputedRef<number> = computed(() => this.#notificationsUnread.value.length)
  readonly #isReady: Ref<boolean> = ref(false)
  readonly #hasUnreadNotifications = computed(() => this.UnreadNotificationCount.value > 0)
  readonly #localNotificationRegistry = ref<Record<Uppercase<string>, Component>>({})
  readonly #notificationReadCount: ComputedRef<number> = computed(() => this.#notificationsRead.value.length)
  readonly #hasReadNotifications = computed(() => this.#notificationsRead.value.length)

  get HasUnreadNotifications() {
    return this.#hasUnreadNotifications
  }

  get Notifications() {
    return this.#notifications
  }

  get IsReady() {
    return this.#isReady
  }

  get UnreadNotifications() {
    return this.#notificationsUnread
  }

  get NotificationCount() {
    return this.#notificationCount
  }

  get UnreadNotificationCount() {
    return this.#notificationUnreadCount
  }

  get ReadNotificationCount() {
    return this.#notificationReadCount
  }

  get ReadNotifications() {
    return this.#notificationsRead
  }

  get HasReadNotifications() {
    return this.#hasReadNotifications
  }

  registerRenderComponent(type: Uppercase<string>, comp: Component) {
    logger.info(`Registered component for ${type}`)
    this.#localNotificationRegistry.value[type] = markRaw(comp)
  }

  getRenderComponent(type?: EmployeeNotificationJsonldDefaultRead['type'] | Uppercase<string>): Component {
    if (typeof type == 'string' && this.#localNotificationRegistry.value[type]) {
      return this.#localNotificationRegistry.value[type]
    }
    switch (type) {
      case 'GENERIC':
        return GenericPanel
      case 'ESTIMATIONREQUEST':
        return EstimationRequestPanel
      case 'EFFORTASSIGNMENT':
        return EffortAssignmentPanel
      case 'EFFORTNOTEMENTION':
        return EffortNoteMentionPanel
      default:
        logger.warn(`Component for ${type} not found`)
        return UnknownTypePanel
    }
  }

  install() {
    const titrop = useTitrop()
    useMercure().subscribe<EmployeeNotificationJsonldDefaultRead>('EmployeeNotification', (data: EmployeeNotificationJsonldDefaultRead) => {
      if (data.employee == titrop.value.user.currentIri) void this.refresh()
    })
    RegisterNotificationPanels()

    useTitropEvents().onLoginSuccess.on(() => {
      void this.refresh()
      this.#isReady.value = true
    })
  }

  async refresh() {
    const allNotifications = await useGeneratedEmployeeNotificationStore().findAll({ query: { itemsPerPage: 100 } }, { forceRefresh: true })
    const sortByDate = (a: EmployeeNotificationJsonldDefaultRead, b: EmployeeNotificationJsonldDefaultRead) => {
      return a.createdAt == b.createdAt ? 0 : DateTime.fromRFC2822(a.createdAt!).toUnixInteger() < DateTime.fromRFC2822(b.createdAt!).toUnixInteger() ? -1 : 1 //same meta, different time
    }
    const allUnreadPrio = allNotifications.value.filter((a) => a.priority && !a.read).sort(sortByDate)
    const allLeft = _.differenceWith(allNotifications.value, allUnreadPrio).sort(sortByDate)
    this.#remoteNotifications.value = [...allUnreadPrio, ...allLeft]
    //        refreshKey.value++
    this.#isReady.value = true
  }

  addLocal<T extends LocalNotificationBaseNonGeneric | LocalNotificationBase<Uppercase<string>>>(notification: T) {
    notification.employee = useTitrop().value.user.currentIri
    notification.deletedAt = null
    notification.read = false
    notification.createdAt = DateTime.now().toISO()
    notification.id = new Date().getTime() + random(0.00000000000001, 0.99999999999999, true)
    useLocalNotificationStore().Add(notification)
  }

  removeLocal(notification: LocalNotificationBaseNonGeneric) {
    useLocalNotificationStore().Remove(notification)
  }

  isLocalNotification(notification: AnyNotification): boolean {
    if (notification['@id']) return false
    return Object.values(useLocalNotificationStore().$data).includes(notification as LocalNotificationBaseNonGeneric)
  }

  async setAsRead(notification: AnyNotification, isRead = true) {
    if (this.isLocalNotification(notification)) {
      notification.read = isRead
    }
    if (notification.read == isRead) return
    await useGeneratedEmployeeNotificationStore().putId({
      path: { id: notification['@id']! },
      body: { read: isRead }
    })
  }
}

export const InAppNotificationService = new _InAppNotificationService()
export default InAppNotificationService

export function useInAppNotificationService() {
  return InAppNotificationService
}
